<template>
  <div class="manage-voucher-login">
    <Modal v-model="isErrorDisplayed">
      <template #header>
        <DynLang
          :by-key="displayedErrorHeaderTransKey"
          capitalize-first
        />
      </template>

      <div class="manage-voucher-login__error">
        <p>
          <DynLang :by-key="displayedErrorTransKey" />
        </p>

        <div class="manage-voucher-login__error__email">
          <a href="mailto:ayuda@spalopia.com">ayuda@spalopia.com</a>
        </div>
      </div>
    </Modal>

    <div
      v-if="loading"
      class="manage-voucher-login__loading"
    >
      <LoadingSpinner />
    </div>

    <div
      v-else
    >
      <div class="manage-voucher-login__header">
        <h2>
          <Lang
            by-key="manage-voucher-title"
            capitalize-first
          />
        </h2>
      </div>

      <div class="manage-voucher-login__form">
        <div class="manage-voucher-login__form__info-side">
          <p>
            <DynLang
              by-key="manage-voucher-info-head"
              capitalize-first
            />
          </p>
          <ul class="manage-voucher-login__form__info-side__list">
            <li>
              <DynLang
                by-key="manage-voucher-info-list-1"
                capitalize-first
              />
            </li>
            <li>
              <DynLang
                by-key="manage-voucher-info-list-2"
                capitalize-first
              />
            </li>
            <li>
              <DynLang
                by-key="manage-voucher-info-list-3"
                capitalize-first
              />
            </li>
            <li>
              <DynLang
                by-key="manage-voucher-info-list-4"
                capitalize-first
              />
            </li>
            <li>
              <DynLang
                by-key="manage-voucher-info-list-5"
                capitalize-first
              />
            </li>
          </ul>
          <p>
            <DynLang
              by-key="manage-voucher-info-foot"
              capitalize-first
            />
          </p>
        </div>

        <div class="manage-voucher-login__form__right-side">
          <div class="manage-voucher-login__form__input">
            <SplInputBox
              v-model="email"
              label-translation-key="manage-voucher-input-email"
              :is-valid="isEmailValid"
              @blur="onEmailBlur"
            >
              <template #error>
                *<DynLang
                  by-key="manage-voucher-email-error"
                  capitalize-first
                />
              </template>
            </SplInputBox>
          </div>

          <div class="manage-voucher-login__form__input">
            <SplInputBox
              v-model="voucherCode"
              label-translation-key="manage-voucher-input-code"
              placeholder="SP-XXXX-XXXX-XXXX-X / BOSPDXXXXXXX"
              :is-valid="isVoucherCodeValid"
              @blur="onVoucherCodeBlur"
            >
              <template #error>
                *<DynLang
                  by-key="manage-voucher-code-error"
                  capitalize-first
                />
              </template>
            </SplInputBox>
          </div>

          <div class="manage-voucher-login__form__input">
            <DatePicker
              v-model="date"
              label-translation-key="manage-voucher-input-date"
            />

            <div v-if="!isDateValid">
              *<DynLang
                by-key="manage-voucher-date-error"
                capitalize-first
              />
            </div>
          </div>

          <div class="manage-voucher-login__form__checkbox">
            <SplCheckbox
              id="input-voucher-offer-approve"
              v-model="allowNotifications"
            >
              <DynLang
                by-key="manage-voucher-accept-offers"
                capitalize-first
              />

              <SplNuxtLink
                class="ml-text link-underline"
                to="/politica-de-privacidad/"
                is-external
                no-lang
              >
                <DynLang
                  by-key="see-privacy-policy"
                  capitalize-first
                />
              </SplNuxtLink>
            </SplCheckbox>
          </div>

          <div>
            <SplButton
              variant="acent"
              size="w-full"
              :disabled="!isLoginButtonEnabled"
              @click="validateAndLogIn"
            >
              <DynLang by-key="manage-voucher-login-btn" />
            </SplButton>
          </div>
        </div>
      </div>

      <Separator hide />

      <div class="manage-voucher-login__faq">
        <div class="manage-voucher-login__faq__header">
          <DynLang
            by-key="manage-voucher-faq-header"
            capitalize-first
          />
        </div>

        <FAQList :items="faqItems" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import LoadingSpinner from '@/core/components/shared/LoadingSpinner.vue'
import SplInputBox from '~/core/components/shared/form/SplInputBox.vue'
import SplEmailInput from '~/core/components/shared/form/SplEmailInput.vue'
import SplButton from '~/core/components/shared/form/SplButton.vue'
import SplCheckbox from '~/core/components/shared/form/SplCheckbox.vue'
import SplNuxtLink from '~/core/components/shared/SplNuxtLink.vue'
import SectionAtom from '~/core/components/shared/Section.atom.vue'
import Separator from '~/core/components/shared/Separator.vue'
import DatePicker from '~/core/components/shared/form/DatePicker.vue'
import Modal from '~/core/components/shared/form/Modal.vue'
import FAQList from '~/core/components/shared/faq/FAQList.vue'

import { useVoucherManagementLogin } from '~/core/composable/voucher-purchase/useVoucherManagementLogin'

import ManageVoucherFAQRepository from '~/core/ts/repository/ManageVoucherFAQRepository'

import { translateByKey } from '~/core/ts/util/translate'
import HeadMetaBuilder from '~/core/ts/util/HeadMetaBuilder'

definePageMeta({
  layout: 'checkout',
})

export default defineNuxtComponent({
  components: {
    LoadingSpinner,
    SplInputBox,
    SplEmailInput,
    SplButton,
    SplCheckbox,
    SplNuxtLink,
    SectionAtom,
    Separator,
    DatePicker,
    Modal,
    FAQList,
  },
  setup() {
    const {
      loading,

      voucherCode,
      date,
      email,
      allowNotifications,

      isVoucherCodeValid,
      isDateValid,
      isEmailValid,

      loginValidation,

      isLoginFormFullyValid,
      isLoginButtonEnabled,

      onVoucherCodeBlur,
      onDateBlur,
      onEmailBlur,

      validateAndLogIn,

      isErrorDisplayed,
      displayedErrorHeaderTransKey,
      displayedErrorTransKey,
    } = useVoucherManagementLogin()

    const title = translateByKey('manage-voucher-shorttitle')
    const builder = HeadMetaBuilder.create()
    builder.withTitleAndMetaTitle(`${title}: Login | Spalopia.com`)
    useHead(builder.get())

    const faqItems = ManageVoucherFAQRepository.get()

    return {
      loading,

      voucherCode,
      date,
      email,
      allowNotifications,

      isVoucherCodeValid,
      isDateValid,
      isEmailValid,

      isLoginFormFullyValid,
      isLoginButtonEnabled,

      loginValidation,

      onVoucherCodeBlur,
      onDateBlur,
      onEmailBlur,

      validateAndLogIn,

      isErrorDisplayed,
      displayedErrorHeaderTransKey,
      displayedErrorTransKey,

      faqItems,
    }
  },
})
</script>

<style lang="scss" scoped>
.manage-voucher-login {
  @apply bg-white;
  @apply p-6;

  @screen lg {
    @apply p-2;
  }

  &__error {
    p {
      @apply py-2;
    }

    &__email {
      @apply p-2;
      @apply text-center;

      @apply text-lg;
      @apply font-bold;

      @apply text-spl-primary-dark;
    }

    &__email:hover {
      @apply text-spl-primary;
    }
  }

  &__loading {
    @apply p-12;
  }

  &__header {
    @apply text-3xl;
    @apply font-bold;

    @apply text-spl-primary-dark;

    @apply text-center;

    @apply py-8;

    @screen md {
      @apply py-16;
      @apply mb-4;
    }
  }

  &__form {
    @apply my-2;

    @apply w-full;
    @apply mx-auto;

    @screen md {
      @apply grid;
      @apply grid-cols-2;

      @apply gap-2;
    }

    @screen lg {
      @apply w-2/3;
    }

    &__input {
      @apply py-2;
      @apply border-b-1;

      @apply border-spl-gray-1;
    }

    &__checkbox {
      @apply py-8;
    }

    &__info-side {
      @apply pb-6;

      &__list {
        @apply py-4;
        @apply ml-8;
        @apply mb-4;

        @apply list-disc;
      }
    }

    &__right-side {
    }
  }

  &__faq {
    @apply py-8;
    @apply mx-auto;

    // @apply mt-8;

    &__header {
      @apply text-2xl;

      @apply text-spl-primary-dark;

      @apply text-center;

      @apply py-4;
    }

    &__item {
      @apply mb-4;
    }

    @screen md {
      @apply mt-12;
    }

    @screen lg {
      @apply w-2/3;
    }
  }
}
</style>
